import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTwin } from '../context/TwinContext';
import { useAuth } from '../context/AuthContext';
import './MyTwins.css';

const MyTwins: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDemo, twins, getActiveTwin, setActiveTwin, deleteTwin } = useTwin();
  const activeTwin = getActiveTwin();
  const [deleting, setDeleting] = useState<string | null>(null);
  const [, setTick] = useState(0);

  // Tick every 3s so health scores update live
  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 3000);
    return () => clearInterval(iv);
  }, []);

  const handleSwitch = (id: string) => { setActiveTwin(id); navigate('/app'); };

  const handleDelete = (id: string) => {
    if (!window.confirm('Delete this twin? This will remove it from the server and cannot be undone.')) return;
    setDeleting(id);
    deleteTwin(id);
    setTimeout(() => {
      setDeleting(null);
      if (activeTwin?.id === id) navigate('/app');
    }, 400);
  };

  const emoji = (type: string) =>
    type === 'cookie' ? '🍪' : type === 'pharma' ? '💊' : type === 'fmcg' ? '🥤' : '🏭';

  if (twins.length === 0) {
    return (
      <div className="my-twins-container">
        <div className="page-header">
          <div>
            <h1>📋 {isDemo ? 'Demo Twins' : 'My Twins'}</h1>
            {!isDemo && user && (
              <p style={{ color: '#9ca3af', fontSize: 14, marginTop: 4 }}>
                Logged in as: <strong>{user.email}</strong>
              </p>
            )}
          </div>
        </div>
        <div className="empty-state">
          <div className="empty-icon">🏭</div>
          <h2>No Twins Yet</h2>
          <p>{isDemo ? 'Demo twin is loading...' : "You haven't created any digital twins yet."}</p>
          {!isDemo && (
            <button className="btn-primary" onClick={() => navigate('/upload-model')}>
              📦 Upload 3D Model →
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="my-twins-container">
      <div className="page-header">
        <div>
          <h1>📋 {isDemo ? 'Demo Twins' : 'My Twins'} ({twins.length})</h1>
          {!isDemo && user && (
            <p style={{ color: '#9ca3af', fontSize: 14, marginTop: 4 }}>
              Logged in as: <strong>{user.email}</strong> • {user.fullName}
            </p>
          )}
        </div>
        {!isDemo && (
          <button className="btn-primary" onClick={() => navigate('/upload-model')}>
            📦 Upload 3D Model
          </button>
        )}
      </div>

      <div className="twins-grid">
        {twins.map(twin => (
          <div
            key={twin.id}
            className={`twin-card ${activeTwin?.id === twin.id ? 'active' : ''} ${deleting === twin.id ? 'deleting' : ''}`}
          >
            {activeTwin?.id === twin.id && <div className="active-badge">✓ Active</div>}

            <div className="twin-header">
              <div className="twin-icon">{emoji(twin.factoryType)}</div>
              <div className="twin-title-section">
                <h3>{twin.name}</h3>
                <p className="twin-company">{twin.company}</p>
              </div>
              <span className={`twin-type ${isDemo ? 'status-demo' : 'status-active'}`}>
                {isDemo ? '📌 Demo' : '✨ Created'}
              </span>
            </div>

            <div className="twin-details">
              <div className="detail">
                <span className="label">Factory Type</span>
                <span className="value" style={{ textTransform: 'capitalize' }}>{twin.factoryType}</span>
              </div>
              <div className="detail">
                <span className="label">Machines</span>
                <span className="value">{twin.machines?.length || 0}</span>
              </div>
              <div className="detail">
                <span className="label">Sensors</span>
                <span className="value">
                  {twin.machines?.reduce((s: number, m) => s + (m.sensors?.length || 0), 0) || 0}
                </span>
              </div>
              <div className="detail">
                <span className="label">Health</span>
                <span className="value" style={{
                  color: twin.healthScore >= 80 ? '#34d399' : twin.healthScore >= 50 ? '#fbbf24' : '#f87171'
                }}>
                  {twin.healthScore}%
                </span>
              </div>
            </div>

            <div className="twin-id"><code>{twin.id}</code></div>

            <div className="twin-actions">
              {activeTwin?.id !== twin.id && (
                <button className="btn-secondary" onClick={() => handleSwitch(twin.id)}>
                  Switch to Twin
                </button>
              )}
              <button className="btn-secondary" onClick={() => { setActiveTwin(twin.id); navigate('/app'); }}>
                Dashboard
              </button>
              <button className="btn-secondary" onClick={() => { setActiveTwin(twin.id); navigate('/app/scene'); }}>
                3D Scene
              </button>
              <button className="btn-secondary" onClick={() => { setActiveTwin(twin.id); navigate('/app/ml'); }}>
                ML
              </button>
              <button className="btn-secondary" onClick={() => { setActiveTwin(twin.id); navigate('/app/alerts'); }}>
                Alerts
              </button>
              {!isDemo && (
                <button className="btn-danger" onClick={() => handleDelete(twin.id)} disabled={deleting === twin.id}>
                  {deleting === twin.id ? '⏳ Deleting...' : '🗑️ Delete'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="twins-info">
        <h3>ℹ️ {isDemo ? 'Demo Mode' : 'About Your Twins'}</h3>
        <ul>
          {isDemo ? (
            <>
              <li>You're viewing the Cookie Factory demo twin</li>
              <li>All data is simulated — no real sensors connected</li>
              <li>Click "Get Started" on the landing page to create your own twins</li>
            </>
          ) : (
            <>
              <li>Your twins are private and only visible to your account ({user?.email})</li>
              <li>Click "Switch to Twin" to load that twin's data across all pages</li>
              <li>Twins are stored in the cloud (DynamoDB) and synced to your browser</li>
              <li>Health score updates in real-time based on sensor simulation</li>
              <li>Each user has their own isolated workspace with separate twins</li>
            </>
          )}
        </ul>
      </div>
    </div>
  );
};

export default MyTwins;
